import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FileCheck2, FileX2, FolderLock } from "lucide-react";
import { CompanyStatus, ROLE_LABEL } from "@/pages/Companies";
import { fileUrl, useApi } from "@/lib/api";

const RETC_ROWS = [
  ["estado_retc", "Estado"], ["fecha_estado_retc", "Fecha del comprobante"], ["rut", "RUT empresa"], ["razon_social", "Nombre empresa"],
  ["nombre_establecimiento", "Nombre del establecimiento"], ["id_establecimiento_vu", "ID establecimiento"], ["direccion", "Dirección"],
  ["comuna", "Comuna"], ["region", "Región"], ["encargado_establecimiento", "Encargado del establecimiento"], ["sistemas_retc", "Sistemas y roles activos"],
];

const Doc = ({ d, cid }) => (
  <div className="border border-slate-200 rounded-2xl p-4 space-y-3" data-testid={`enabling-doc-${cid}-${d.type}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="flex items-center gap-2 text-sm font-medium text-[#05130e]">
        {d.file_id ? <FileCheck2 className="w-4 h-4 shrink-0 text-[#00b37a]" /> : <FileX2 className="w-4 h-4 shrink-0 text-slate-400" />}{d.label}
      </p>
      {d.file_id
        ? <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#00915f] hover:underline" data-testid={`enabling-doc-open-${cid}-${d.type}`}>Ver PDF <ExternalLink className="w-3.5 h-3.5" /></a>
        : <span className="text-xs text-slate-400">No subido</span>}
    </div>
    {d.type === "retc" && d.file_id && (
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm bg-[#f4f7f6] rounded-xl p-4" data-testid={`enabling-retc-data-${cid}`}>
        {RETC_ROWS.map(([k, label]) => (
          <div key={k} className={k === "sistemas_retc" ? "sm:col-span-2" : ""}>
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className={`font-medium ${k === "estado_retc" ? (d.resumen[k] === "ACTIVO" ? "text-[#007a50]" : "text-red-600") : "text-[#05130e]"}`} data-testid={`enabling-retc-${k}-${cid}`}>{d.resumen[k] || "—"}</dd>
          </div>
        ))}
      </dl>
    )}
    {d.file_id && <p className="text-[11px] text-slate-400">{d.file_name} · subido el {new Date(d.uploaded_at).toLocaleDateString("es-CL")}</p>}
  </div>
);

export const EnablingDocs = () => {
  const api = useApi();
  const [list, setList] = useState(null);

  useEffect(() => {
    api("get", "/me/documentos-habilitadores").then((r) => setList(r.data)).catch(() => setList([])).finally(() => {
      if (window.location.hash === "#documentos") setTimeout(() => document.getElementById("documentos")?.scrollIntoView({ behavior: "smooth" }), 100);
    });
  }, [api]);

  return (
    <section id="documentos" className="scroll-mt-24 bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6" data-testid="enabling-docs-section">
      <div>
        <h2 className="flex items-center gap-2 font-semibold text-lg text-[#05130e]"><FolderLock className="w-5 h-5 text-[#00b37a]" />Documentos Habilitadores</h2>
        <p className="text-sm text-slate-500 mt-1">Documentos oficiales que habilitan a cada empresa para operar en la plataforma. El Comprobante de Registro Ventanilla Única (RETC) con estado ACTIVO habilita a la Empresa Generadora para publicar lotes en el marketplace.</p>
      </div>
      {list?.length === 0 && <p className="text-sm text-slate-500" data-testid="enabling-docs-empty">Aún no tienes empresas. <Link to="/mis-empresas" className="text-[#00915f] font-semibold hover:underline">Agrega una en Mis empresas</Link>.</p>}
      {list?.map((c) => (
        <div key={c.id} className="space-y-3" data-testid={`enabling-company-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to={`/mis-empresas/${c.id}`} className="font-semibold text-[#05130e] hover:text-[#00915f]">{c.resumen?.razon_social || "Empresa sin razón social"} <span className="ml-1 text-[11px] font-mono uppercase text-[#00915f]">{ROLE_LABEL[c.role]}</span></Link>
            <CompanyStatus status={c.status} />
          </div>
          {c.documents.map((d) => <Doc key={d.type} d={d} cid={c.id} />)}
        </div>
      ))}
    </section>
  );
};
